import { TFunction } from 'i18next';
import { AIActionKind, AIProcessActionTemplate, AIPromptActionTemplate } from 'models/copilot/actions/aiActionTemplate';
import { TenantProcess } from 'models/copilot/tenantProcessStateBase';

/**
 * The ready-made questions one scene of the Generic usage offers. They live here, not next to the scene,
 * because the Generic definition binds them and components do not import scenes. One template per question:
 * each label and prompt is a literal translation key.
 */

// All tasks
export const tasksWorkloadTemplate: AIPromptActionTemplate = {
  id: 'tasks.workload',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.AllTasks.Workload.Label');
  },
  getPrompt(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.AllTasks.Workload.Prompt');
  },
};

// Dashboard: the summarize-my-work skill reads my tasks, my requests, my approvals and my activities and ranks the
// five that need me most (PLAN-072)
export const dashboardSummarizeWorkTemplate: AIPromptActionTemplate = {
  id: 'dashboard.summarizeWork',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.Dashboard.SummarizeWork.Label');
  },
  getPrompt(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.Dashboard.SummarizeWork.Prompt');
  },
};

// Dashboard: the create-task skill finds the incident template and tag (concept_get)
export const dashboardRecordEventTemplate: AIPromptActionTemplate = {
  id: 'dashboard.recordEvent',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.Dashboard.RecordEvent.Label');
  },
  getPrompt(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.Dashboard.RecordEvent.Prompt');
  },
};

// My tasks, My requests, All tasks and the Library: the tasks-from-report skill turns the findings of the
// session's document into tasks on one approval
export const tasksFromReportTemplate: AIPromptActionTemplate = {
  id: 'tasks.fromReport',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.Tasks.FromReport.Label');
  },
  getPrompt(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.Tasks.FromReport.Prompt');
  },
};

// Risks list: the risk-gap-analysis skill, read only
export const riskGapAnalysisTemplate: AIPromptActionTemplate = {
  id: 'risks.gapAnalysis',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.Risks.GapAnalysis.Label');
  },
  getPrompt(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.Risks.GapAnalysis.Prompt');
  },
};

// Controls, Requirements and the frameworks overview: the control-evidence-gaps skill, read only
export const controlEvidenceGapsTemplate: AIPromptActionTemplate = {
  id: 'controls.evidenceGaps',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.Controls.EvidenceGaps.Label');
  },
  getPrompt(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.Controls.EvidenceGaps.Prompt');
  },
};

// Objective details: proposes KPIs the user adds; a created KPI is not linked to the objective
export const objectiveSuggestKpisTemplate: AIPromptActionTemplate = {
  id: 'objective.suggestKpis',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Objective.SuggestKpis.Label');
  },
  getPrompt(t: TFunction<string[]>) {
    return t('copilot:Actions.Objective.SuggestKpis.Prompt');
  },
};

// Asset details: fills the empty website, contact e-mail, container, classifications and description, each change
// staged for approval
export const assetAdditionalDetailsTemplate: AIPromptActionTemplate = {
  id: 'asset.additionalDetails',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Asset.AdditionalDetails.Label');
  },
  getPrompt(t: TFunction<string[]>) {
    return t('copilot:Actions.Asset.AdditionalDetails.Prompt');
  },
};

// Control details: stages a monitoring task for approval
export const controlPlanCheckTemplate: AIPromptActionTemplate = {
  id: 'control.planCheck',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Control.PlanCheck.Label');
  },
  getPrompt(t: TFunction<string[]>) {
    return t('copilot:Actions.Control.PlanCheck.Prompt');
  },
};

// KPI data: reads the data points (kpi_data_get)
export const kpiAnalyzeTemplate: AIPromptActionTemplate = {
  id: 'kpi.analyze',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.KPI.Analyze.Label');
  },
  getPrompt(t: TFunction<string[]>) {
    return t('copilot:Actions.KPI.Analyze.Prompt');
  },
};

// The KPI report as an AI process (PLAN-070): the scene starts it with the selected KPIs (none selected: the card
// asks), the data report modal with its finished job. One template for both, so the two requests resolve to the
// one action the KPIs scene lists.
export const kpiReportStartTemplate: AIProcessActionTemplate = {
  id: 'kpis.report',
  kind: AIActionKind.Process,
  acceptsSelection: true,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Scenes.KPIs.Visualize.Label');
  },
  getProcessId() {
    return TenantProcess.KpiReport;
  },
};
